import getHashParams from "../utils/getHashParams";
import { isVisible } from "../utils/isVisible";

const TAB_CLASS_NAME = ".comp-tab";

export default class MagazineTabs {
  element: HTMLElement;
  latestHash: string;
  tabCursorMark: string;
  activeTab: string;
  tabInitialized: boolean;

  constructor(ctx: HTMLElement) {
    const tabComponents = document.querySelectorAll(".comp-tab");

    this.element = ctx;
    this.latestHash = "";
    this.tabCursorMark = "";
    this.activeTab = tabComponents.length > 0 ? tabComponents[0].id : "";
    this.addEventListeners();

    window.addEventListener("hashchange", () => {
      const currentTabId = this._getIdOfCurrentTab(this.element);
      if (
        currentTabId === getHashParams("tab") &&
        (this.activeTab !== currentTabId ||
          this.latestHash !== window.location.hash.substr(1))
      ) {
        this.getHistoryData();
      }
    });

    // Deep-link restore is handled centrally after `load` by the coordinator in
    // Tabs.ts, which replays the entry hash through the `hashchange` listener
    // above. Doing it here would race the tab-container init.

    if (this.activeTab === "tab_authors" || this.activeTab === "tab_channels") {
      this.triggerTabClick(this.activeTab);
    }

    this.tabInitialized = this._isVisible();
  }

  onShowTab(event) {
    const data = event?.detail;

    if (!data) {
      return;
    }

    this.activeTab = data.id;
    if (this._getIdOfCurrentTab(this.element) === data.id) {
      if (!this.tabInitialized) {
        this.tabInitialized = true;
        this.tabCursorMark = data.id;
      }
      this.setHistoryData();
    }
  }

  addEventListeners() {
    document.addEventListener("tabItemShown", this.onShowTab.bind(this));
  }

  setHistoryData() {
    if (!this.tabCursorMark || this.tabCursorMark === "") {
      this.tabCursorMark = this._getIdOfCurrentTab(this.element);
    }
    const params = new URLSearchParams({ tab: this.tabCursorMark });

    this.latestHash = params.toString();

    window.location.hash = this.latestHash;
  }

  getHistoryData() {
    const tab = getHashParams("tab") || Object.keys(getHashParams())[0];

    if (this._getIdOfCurrentTab(this.element) === tab) {
      this.triggerTabClick(tab);
    }
  }

  triggerTabClick(tab: string) {
    const tabItem = document.querySelector<HTMLLinkElement>("a[href$='" + tab + "']");
    if (!tabItem) return;

    // The tab link is an <a href="#<tab-id>">. Its default navigation would
    // overwrite the full "#tab=…&tag=…" hash with the short "#<tab-id>" form,
    // dropping the tag and leaving no articles. The tab-container
    // preventDefault()s this once initialised, but there is an init-order race,
    // so guard the click here regardless of the tab-container's state.
    const preventNavigation = (e: Event) => e.preventDefault();
    tabItem.addEventListener("click", preventNavigation);
    tabItem.click();
    tabItem.removeEventListener("click", preventNavigation);
  }

  _getIdOfCurrentTab(element: HTMLElement) {
    return element.closest(TAB_CLASS_NAME).getAttribute("id");
  }

  _isVisible() {
    return isVisible(this.element);
  }
}